import { message, Modal } from "antd";
import moment from "moment";
import axios from "axios";
import FileSaver from "file-saver";
import { AsyncZippable, zip } from "fflate";
import OrderDTO from "@/dto/order/OrderDTO";
import { uuid } from "@/utils";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import {
    ParsedCustomizationType,
    parseOrderItemCustomizationContent,
    SEOrderParsedCustomizationInfo,
} from "@/dto/order/CustomizationInfoDTO";

enum DownloadCustomizationRule {
    CUSTOMIZATION_INFO = "CUSTOMIZATION_INFO",
    ORIGINAL_ID = "ORIGINAL_ID",
    ORDER_ITEM_INDEX = "ORDER_ITEM_INDEX",
    BUYER_NICKNAME = "BUYER_NICKNAME",
    SKU_INDEX = "SKU_INDEX",
    QUANTITY = "QUANTITY",
}

const SKIP_IMAGE_URL = "https://img.alicdn.com/imgextra/i2/54458674/O1CN012fCo0O2Dwk6TA1e4A_!!54458674.png";
const defaultNameRules = [
    DownloadCustomizationRule.CUSTOMIZATION_INFO,
    DownloadCustomizationRule.ORIGINAL_ID,
    DownloadCustomizationRule.ORDER_ITEM_INDEX,
    DownloadCustomizationRule.BUYER_NICKNAME,
    DownloadCustomizationRule.SKU_INDEX,
    DownloadCustomizationRule.QUANTITY,
];

interface ToDownloadCustomization {
    order: OrderDTO;
    orderItem: OrderItemDTO;
    orderItemIndex: number;
    customizationInfo: SEOrderParsedCustomizationInfo;
    customizationInfoIndex: number;
}

function getToDownloadData(orders: OrderDTO[]) {
    const toDownloads: ToDownloadCustomization[] = [];

    orders.forEach((order) => {
        order.orderItems
            // @ts-ignore
            .filter((it) => !!it.isSelected)
            .forEach((orderItem, orderItemIndex) => {
                try {
                    const result = parseOrderItemCustomizationContent(orderItem?.customizationInfo?.content);

                    if (result.type === ParsedCustomizationType.LH_CUSTOMIZATION_V1) {
                        result.customizationInfos.forEach((customization, customizationIndex) => {
                            const data: ToDownloadCustomization = {
                                order: order,
                                orderItem: orderItem,
                                orderItemIndex: orderItemIndex,
                                customizationInfo: customization,
                                customizationInfoIndex: customizationIndex,
                            };

                            toDownloads.push(data);
                        });
                    } else if (result.type === ParsedCustomizationType.PHONE_CASE_CUSTOMIZATION_V2) {
                        result.phoneCaseCustomizationInfos.forEach((customization, customizationIndex) => {
                            const data: ToDownloadCustomization = {
                                order: order,
                                orderItem: orderItem,
                                orderItemIndex: orderItemIndex,
                                customizationInfo: {
                                    brand: customization.phoneBrandName,
                                    model: customization.phoneModelName,
                                    material: customization.phoneCaseMaterialName,
                                    bgColorName: customization.phoneCaseMaterialColorName,
                                    pic: customization.customizedImageUrl,
                                    effectPic: customization.previewImageUrl,
                                    quantity: 1,
                                },
                                customizationInfoIndex: customizationIndex,
                            };

                            toDownloads.push(data);
                        });
                    }
                } catch (error) {}
            });
    });

    return toDownloads;
}

// async function getConfigRecordThicknessMapping(toDownloads: ToDownloadData[]) {
//     /**
//      * `cellPhoneModelId|cellPhoneCaseMaterialId`  -> thickness
//      */
//     const configRecordThicknessMapping = new Map<string, number>();

//     const getConfigRecordParams = toDownloads
//         .map((download) => {
//             if (download.type === CustomizationType.CELL_PHONE_CUSTOMIZATION) {
//                 const modelId = download.cellPhoneCustomizationInfo.modelId;
//                 const caseMaterialId = download.cellPhoneCustomizationInfo.caseMaterialId;

//                 if (!(modelId && caseMaterialId)) {
//                     return undefined;
//                 }

//                 return {
//                     modelId: download.cellPhoneCustomizationInfo.modelId,
//                     caseMaterialId: download.cellPhoneCustomizationInfo.caseMaterialId,
//                 };
//             }

//             return undefined;
//         })
//         .filter((it) => !!it);

//     const uniqGetConfigRecordParams = uniqBy(getConfigRecordParams, (it) => {
//         return `${it.modelId}|${it.caseMaterialId}`;
//     });

//     if (uniqGetConfigRecordParams?.length) {
//         const configRecords = await getBatchConfigRecord(uniqGetConfigRecordParams);

//         configRecords.forEach((configRecord) => {
//             const key = [configRecord.cellPhoneModelId, configRecord.cellPhoneCaseMaterialId].join("|");
//             const value = configRecord.thickness || 1;

//             configRecordThicknessMapping.set(key, value);
//         });
//     }

//     return configRecordThicknessMapping;
// }

function processToDownloadData(toDownloads: ToDownloadCustomization[], nameRules: DownloadCustomizationRule[]) {
    return toDownloads
        .map((download) => {
            const { order, orderItem, orderItemIndex, customizationInfo, customizationInfoIndex } = download;

            let downloadFileUrl = customizationInfo.ossUrl || customizationInfo.pic;

            const filename = nameRules
                .map((rule) => {
                    if (rule === DownloadCustomizationRule.CUSTOMIZATION_INFO) {
                        const commonInfoName = [customizationInfo.bottomPanelName, customizationInfo.aspectName].filter(
                            (it) => !!it,
                        );

                        const phoneInfoName = [
                            customizationInfo.brand,
                            customizationInfo.model,
                            customizationInfo.material,
                            customizationInfo.bgColorName,
                        ].filter((it) => !!it);

                        if (commonInfoName.join("")) {
                            return commonInfoName.join("-");
                        }

                        return phoneInfoName.join("-");
                    } else if (rule === DownloadCustomizationRule.ORIGINAL_ID) {
                        return order.originalId;
                    } else if (rule === DownloadCustomizationRule.ORDER_ITEM_INDEX) {
                        return orderItemIndex + 1;
                    } else if (rule === DownloadCustomizationRule.BUYER_NICKNAME) {
                        return order.buyerNickname || order.buyerUsername || "匿名买家";
                    } else if (rule === DownloadCustomizationRule.SKU_INDEX) {
                        return (customizationInfoIndex + 1).toString().padStart(2, "0");
                    } else if (rule === DownloadCustomizationRule.QUANTITY) {
                        return `定制${customizationInfo.quantity}张`;
                    }

                    return "";
                })
                .join("-");

            return {
                subDir: "",
                filename,
                url: downloadFileUrl,
                orderOriginalId: order.originalId,
            };
        })
        .filter((it) => !!it);
}

/**
 * 批量下载定制图
 * 每500m做一次分包
 */
export default async function batchDownloadCustomizationImages(orders: OrderDTO[]) {
    const key = uuid();

    message.open({
        content: `准备批量下载定制图中...`,
        duration: 0,
        type: "loading",
        key,
    });

    try {
        const toDownloads = getToDownloadData(orders);

        const toSaveDownloadInfos = processToDownloadData(toDownloads, defaultNameRules);

        if (!toSaveDownloadInfos.length) {
            return Modal.info({
                title: "选中的订单未包含来火定制图",
            });
        }

        let fileObj: AsyncZippable = {};
        const downloadErrorMessages = [];

        const downloadTile = moment().format("YYYY-MM-DD HH_mm_ss");
        let chunkTime = 1;
        let chunkSize = 0;

        for (let i = 0; i < toSaveDownloadInfos.length; i++) {
            if (chunkSize > 500 * 1024 * 1024) {
                let zipFileName = ["定制图", downloadTile].join("-");
                zipFileName += ` 分包(${chunkTime})`;
                zipFileName += `.zip`;

                await saveZipFile(fileObj, zipFileName);

                chunkTime++;
                chunkSize = 0;
                fileObj = {};
            }

            const info = toSaveDownloadInfos[i];

            try {
                const imageResponse = await axios.get<ArrayBuffer>(info.url, {
                    responseType: "arraybuffer",
                });

                const contentType = imageResponse.headers["content-type"];

                const matchType = /image\/(.+)/.exec(contentType);

                const imageType = matchType?.[1]?.trim() || "png";

                const trueFileName = `${info.filename}.${imageType}`;

                const zipFilePath = [info.subDir, trueFileName].filter((it) => !!it).join("/");

                chunkSize += imageResponse.data.byteLength;

                fileObj[zipFilePath] = [
                    new Uint8Array(imageResponse.data),
                    {
                        level: 0,
                    },
                ];

                message.open({
                    content: `下载定制图中${(((i + 1) / toSaveDownloadInfos.length) * 100).toFixed(2)}%`,
                    duration: 0,
                    type: "loading",
                    key,
                });
            } catch (error) {
                console.log(error, error?.request);
                const message = error?.message || error;

                downloadErrorMessages.push(`${info.orderOriginalId}失败原因:${message}`);
            }
        }

        // 全部错误
        if (downloadErrorMessages.length === toSaveDownloadInfos.length) {
            Modal.error({
                title: "下载定制图错误",
                content: downloadErrorMessages.join("\n"),
            });

            return;
        }

        message.success("下载任务处理完成");

        if (downloadErrorMessages.length) {
            Modal.error({
                title: "下载部分定制图发生错误",
                content: downloadErrorMessages.join("\n"),
            });
        }

        message.open({
            content: `定制图打包中...`,
            duration: 0,
            type: "loading",
            key,
        });

        let zipFileName = ["定制图", downloadTile].join("-");

        if (chunkTime > 1) {
            zipFileName += ` 分包(${chunkTime})`;

            Modal.info({
                title: "温馨提示",
                content: `因定制图太多，系统已分成${chunkTime}个压缩包下载，请注意查看！`,
            });
        }

        await saveZipFile(fileObj, zipFileName + ".zip");
    } catch (error) {
        Modal.destroyAll();

        const message = error?.message || error;

        Modal.error({
            title: "下载定制图发生错误",
            content: message,
        });
    } finally {
        message.destroy(key);
    }
}

async function retry<T extends Function>(fn: T, retryTime: number) {
    let time = 0;

    while (true) {
        try {
            return await fn();
        } catch (error) {
            if (time > retryTime) {
                throw error;
            }

            time++;
        }
    }
}

async function saveZipFile(fileObj: any, fileName: string) {
    const data = await new Promise<Uint8Array>((yes, no) => {
        zip(fileObj, (error, data) => {
            if (error) {
                return no(error);
            }

            return yes(data);
        });
    });

    FileSaver.saveAs(new Blob([data]), fileName);
}
